import React, { useEffect, useMemo, useState } from 'react';
import TitleText from '../common/TitleText';
import SpecsNote from '../common/SpecsNote';
import { VIDEO_CDN } from '../../content/videos';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import {
    ArrowRight, Plus, MapPin, Phone, Clock, MessageCircle,
    Award, Ruler, Wrench, ShoppingBag, Zap, Cpu, Battery, Mountain, Activity, Droplet,
    Settings, Disc, Shield, Sun, Hand, Glasses, Shirt, GripVertical, Wind, Apple, Watch
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { slugify, formatPrice } from '../../utils';
import seo from '../../seo/seoConfig.json';
import seriesContent from '../../content/seriesContent.json';
import { RIDER_HEIGHTS, cmToFeet, GEOMETRY_SOURCE } from '../../data/geometry';
import ServiceMarketing from '../home/ServiceMarketing';

const { business } = seo;
const WHATSAPP_URL = `https://wa.me/18297163555?text=${encodeURIComponent('Hola, vi su página y me gustaría más información.')}`;

const ICONS = {
    award: Award, ruler: Ruler, wrench: Wrench, bag: ShoppingBag, zap: Zap, cpu: Cpu, battery: Battery,
    mountain: Mountain, activity: Activity, droplet: Droplet, settings: Settings, disc: Disc, shield: Shield,
    sun: Sun, hand: Hand, glasses: Glasses, shirt: Shirt, grip: GripVertical, wind: Wind, apple: Apple, watch: Watch
};

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};
const inView = { initial: 'hidden', whileInView: 'show', viewport: { once: true, amount: 0.2 } };

const SectionTitle = ({ children, words, text, light }) => (
    <motion.div variants={fadeUp} {...inView} className="mb-14 max-w-3xl">
        <h2 className={`text-3xl md:text-5xl font-black uppercase italic tracking-tight leading-none ${light ? 'text-white' : 'text-black'}`}><TitleText text={children} words={words} dark={!light} /></h2>
        {text && <p className={`mt-5 text-lg leading-relaxed ${light ? 'text-zinc-400' : 'text-gray-600'}`}>{text}</p>}
    </motion.div>
);

const CtaButton = ({ cta, light }) => {
    const outline = cta.style === 'outline';
    const className = outline
        ? `border ${light ? 'border-white text-white hover:bg-white/10' : 'border-black text-black hover:bg-black hover:text-white'} backdrop-blur-sm`
        : `${light ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-zinc-800'}`;
    const base = `${className} px-8 py-4 text-sm font-bold uppercase tracking-widest transition-all transform hover:-translate-y-1 inline-flex items-center justify-center gap-3`;
    if (cta.href === 'maps') return <a href={business.mapsUrl} target="_blank" rel="noopener noreferrer" className={base}><MapPin className="w-4 h-4" />{cta.label}</a>;
    if (cta.href === 'whatsapp') return <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={base}><MessageCircle className="w-4 h-4" />{cta.label}</a>;
    return <Link to={cta.to} className={base}>{cta.label}</Link>;
};

// Data shared by the bike-based sections: series and bikes from Supabase, loaded once per page.
export const useCatalog = () => {
    const [catalog, setCatalog] = useState({ series: [], bikes: [], loaded: false });
    useEffect(() => {
        let active = true;
        Promise.all([
            supabase.from('series').select('id, nombre, categoria_id'),
            supabase.from('bicicletas').select('id, modelo, serie_id, precio_eur, imagenes_urls')
        ]).then(([seriesRes, bikesRes]) => {
            if (active) setCatalog({ series: seriesRes.data || [], bikes: bikesRes.data || [], loaded: true });
        });
        return () => { active = false; };
    }, []);
    return catalog;
};

export const Hero = ({ section }) => {
    const productStyle = section.imageStyle === 'product';
    return (
        <section className={`relative w-full ${productStyle ? 'min-h-[90vh]' : 'h-[90vh] min-h-[600px]'} overflow-hidden bg-black`}>
            {section.video && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <iframe src={section.video} title="Video" allow="autoplay; fullscreen" className="absolute top-1/2 left-1/2 w-[177.77vh] min-w-full min-h-full h-[56.25vw] -translate-x-1/2 -translate-y-1/2" />
                </div>
            )}
            {section.videoFile && (
                <video autoPlay loop muted playsInline poster={section.videoPoster && `${VIDEO_CDN}/${section.videoPoster}`} className="absolute inset-0 w-full h-full object-cover">
                    <source src={`${VIDEO_CDN}/${section.videoFile}`} type="video/mp4" />
                </video>
            )}
            {section.image && !productStyle && (
                <motion.img initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2.5, ease: 'easeOut' }} src={section.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            )}
            {productStyle && <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-black"><div className="absolute right-0 top-1/4 w-[60vw] h-[60vw] bg-purple-600/20 blur-[140px] rounded-full" /></div>}
            {!productStyle && <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />}

            <div className={`relative h-full max-w-[1600px] mx-auto px-6 md:px-12 flex ${productStyle ? 'flex-col lg:flex-row items-center py-32 gap-10' : 'items-center pt-20'}`}>
                <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="text-white max-w-2xl">
                    <span className="inline-block py-1 px-3 border border-white/30 text-xs font-bold uppercase tracking-[0.2em] mb-6 backdrop-blur-sm">{section.eyebrow}</span>
                    <h1 className="text-5xl md:text-7xl font-black uppercase italic leading-[0.95] mb-8 tracking-tight">
                        {section.title}<br />
                        <span className="gradient-text">{section.highlight}</span>
                    </h1>
                    <p className="text-lg md:text-xl text-gray-200 mb-10 max-w-xl font-light leading-relaxed">{section.text}</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        {section.ctas?.map((cta) => <CtaButton key={cta.label} cta={cta} light />)}
                    </div>
                </motion.div>
                {productStyle && section.image && (
                    <motion.img
                        initial={{ opacity: 0, x: 120 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1.1, delay: 0.2, ease: 'easeOut' }}
                        src={section.image}
                        alt=""
                        className="w-full lg:w-1/2 max-h-[480px] object-contain drop-shadow-[0_30px_50px_rgba(0,0,0,0.6)]"
                    />
                )}
            </div>
        </section>
    );
};

export const Highlights = ({ section }) => (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24">
        <SectionTitle words={section.titleWords} text={section.text}>{section.title}</SectionTitle>
        <motion.div {...inView} transition={{ staggerChildren: 0.12 }} className={`grid grid-cols-1 sm:grid-cols-2 ${section.items.length > 3 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
            {section.items.map((item) => {
                const Icon = ICONS[item.icon] || Award;
                return (
                    <motion.div key={item.title} variants={fadeUp} className="group border border-zinc-200 rounded-2xl p-8 hover:border-black hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-white">
                        <div className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center mb-6 group-hover:bg-black group-hover:text-white transition-colors">
                            <Icon className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-black italic uppercase tracking-tight mb-3">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.text}</p>
                    </motion.div>
                );
            })}
        </motion.div>
    </section>
);

export const Categories = ({ section, catalog }) => {
    const countFor = (ids) => {
        const seriesIds = catalog.series.filter((s) => ids.includes(s.categoria_id)).map((s) => s.id);
        return catalog.bikes.filter((b) => seriesIds.includes(b.serie_id)).length;
    };
    const imageFor = (ids) => {
        const seriesIds = catalog.series.filter((s) => ids.includes(s.categoria_id)).map((s) => s.id);
        return catalog.bikes.find((b) => seriesIds.includes(b.serie_id) && b.imagenes_urls?.[0])?.imagenes_urls[0];
    };
    return (
        <section className="bg-zinc-50 py-24">
            <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                <SectionTitle words={section.titleWords} text={section.text}>{section.title}</SectionTitle>
                <motion.div {...inView} transition={{ staggerChildren: 0.08 }} className={`grid grid-cols-1 sm:grid-cols-2 ${section.items.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
                    {section.items.map((item) => {
                        const image = imageFor(item.categoryIds);
                        const count = countFor(item.categoryIds);
                        return (
                            <motion.div key={item.label} variants={fadeUp}>
                                <Link to={item.to} className="group relative block bg-white rounded-3xl overflow-hidden border border-zinc-200 hover:border-black transition-colors">
                                    <div className="aspect-[16/10] flex items-center justify-center p-6 bg-gradient-to-b from-white to-zinc-100">
                                        {image && <img loading="lazy" decoding="async" src={image} alt={item.label} className="max-h-full max-w-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-700" />}
                                    </div>
                                    <div className="p-6 flex items-end justify-between">
                                        <div>
                                            <h3 className="text-2xl font-black uppercase italic tracking-tight">{item.label}</h3>
                                            <p className="text-gray-500 text-sm">{item.text}{catalog.loaded && count > 0 ? ` · ${count} modelos` : ''}</p>
                                        </div>
                                        <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
};

export const Bikes = ({ section, catalog }) => {
    const bikes = useMemo(() => {
        const result = [];
        section.categoryIds.forEach((catId) => {
            catalog.series.filter((s) => s.categoria_id === catId).forEach((serie) => {
                const withImage = catalog.bikes.filter((b) => b.serie_id === serie.id && b.imagenes_urls?.[0]);
                result.push(...withImage.slice(0, section.perSeries || 1));
            });
        });
        return result.slice(0, 14);
    }, [section.categoryIds, section.perSeries, catalog]);

    return (
        <section className="bg-black text-white py-24 overflow-hidden">
            <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                <SectionTitle words={section.titleWords} text={section.text} light>{section.title}</SectionTitle>
            </div>
            {bikes.length > 0 && (
                <Swiper
                    modules={[Navigation, Autoplay]}
                    navigation
                    autoplay={{ delay: 3500, disableOnInteraction: true }}
                    loop={bikes.length > 4}
                    spaceBetween={24}
                    slidesPerView={1.2}
                    centeredSlides
                    breakpoints={{ 640: { slidesPerView: 2.2 }, 1024: { slidesPerView: 3.3 }, 1440: { slidesPerView: 4.2 } }}
                    className="landing-bikes !px-6"
                >
                    {bikes.map((bike) => (
                        <SwiperSlide key={bike.id}>
                            <Link to={`/product/${slugify(bike.modelo)}`} className="group block rounded-3xl bg-zinc-900 border border-zinc-800 hover:border-zinc-500 transition-colors p-6">
                                <div className="aspect-[4/3] flex items-center justify-center mb-6">
                                    <img loading="lazy" decoding="async" src={bike.imagenes_urls[0]} alt={`Raymon ${bike.modelo}`} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />
                                </div>
                                <h3 className="text-xl font-black italic uppercase tracking-tight">{bike.modelo}</h3>
                                <p className="text-zinc-400 font-medium">{formatPrice(bike.precio_eur, 'bikes')}</p>
                            </Link>
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}
            <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-14 space-y-8">
                {section.cta && <CtaButton cta={section.cta} light />}
                <SpecsNote light />
            </div>
        </section>
    );
};

export const Series = ({ section, catalog }) => {
    const items = section.slugs.map((slug) => {
        const content = seriesContent[slug];
        const serie = catalog.series.find((s) => slugify(s.nombre) === slug);
        const image = serie && catalog.bikes.find((b) => b.serie_id === serie.id && b.imagenes_urls?.[0])?.imagenes_urls[0];
        return content && { slug, content, image };
    }).filter(Boolean);

    return (
        <section className={`${section.dark ? 'bg-zinc-950 text-white' : 'bg-white'} py-24`}>
            <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                <SectionTitle words={section.titleWords} text={section.text} light={section.dark}>{section.title}</SectionTitle>
                <motion.div {...inView} transition={{ staggerChildren: 0.1 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {items.map(({ slug, content, image }) => (
                        <motion.div key={slug} variants={fadeUp}>
                            <Link to={`/series/${slug}`} className={`group block rounded-3xl overflow-hidden border ${section.dark ? 'border-zinc-800 hover:border-zinc-500 bg-zinc-900' : 'border-zinc-200 hover:border-black bg-zinc-50'} transition-colors`}>
                                <div className="relative aspect-[16/10] flex items-center justify-center p-8">
                                    <span className={`absolute inset-0 flex items-center justify-center text-[5rem] md:text-[6rem] font-black uppercase italic select-none ${section.dark ? 'text-white/[0.04]' : 'text-black/[0.04]'}`}>{content.nombre}</span>
                                    {image && <img loading="lazy" decoding="async" src={image} alt={`Raymon ${content.nombre}`} className={`relative max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-700 ${section.dark ? '' : 'mix-blend-multiply'}`} />}
                                </div>
                                <div className="p-6">
                                    <span className={`text-xs font-bold uppercase tracking-[0.2em] ${section.dark ? 'text-zinc-500' : 'text-gray-500'}`}>{content.tipo}</span>
                                    <h3 className="text-2xl font-black uppercase italic tracking-tight mt-1 mb-2">Raymon {content.nombre}</h3>
                                    <p className={`${section.dark ? 'text-zinc-400' : 'text-gray-600'} leading-relaxed`}>{content.eslogan}</p>
                                    <span className="inline-flex items-center gap-2 mt-5 text-xs font-bold uppercase tracking-widest">Ver serie <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" /></span>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

// "Desde USD $X" per category, computed from the catalog at load time so the figures never go stale.
export const Prices = ({ section, catalog }) => {
    const items = section.items.map((item) => {
        const seriesIds = catalog.series.filter((s) => item.categoryIds.includes(s.categoria_id)).map((s) => s.id);
        const prices = catalog.bikes
            .filter((b) => seriesIds.includes(b.serie_id))
            .map((b) => parseFloat(Array.isArray(b.precio_eur) ? b.precio_eur[0] : b.precio_eur))
            .filter((p) => p > 0);
        return { ...item, min: prices.length ? Math.min(...prices) : null, count: prices.length };
    });
    const money = (n) => `USD $${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n)}`;
    return (
        <section className="bg-zinc-950 text-white py-24">
            <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                <SectionTitle words={section.titleWords} text={section.text} light>{section.title}</SectionTitle>
                <motion.div {...inView} transition={{ staggerChildren: 0.08 }} className={`grid grid-cols-1 sm:grid-cols-2 ${items.length > 4 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-6`}>
                    {items.map((item) => (
                        <motion.div key={item.label} variants={fadeUp}>
                            <Link to={item.to} className="group flex flex-col justify-between h-full rounded-3xl border border-zinc-800 bg-zinc-900 p-8 hover:border-zinc-500 transition-colors">
                                <div>
                                    <h3 className="text-2xl font-black uppercase italic tracking-tight">{item.label}</h3>
                                    <p className="text-zinc-400 mt-1">{item.text}</p>
                                </div>
                                <div className="mt-8 flex items-end justify-between gap-4">
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Desde</div>
                                        <div className="text-3xl md:text-4xl font-black leading-none mt-1">{!catalog.loaded ? '…' : item.min ? money(item.min) : 'Consultar'}</div>
                                        {catalog.loaded && item.count > 0 && <div className="text-sm text-zinc-500 mt-2">{item.count} {item.count === 1 ? 'modelo' : 'modelos'}</div>}
                                    </div>
                                    <ArrowRight className="w-6 h-6 shrink-0 group-hover:translate-x-2 transition-transform" />
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>
                <p className="mt-10 text-sm text-zinc-500 max-w-3xl">
                    Precios en dólares (USD) tomados de nuestro catálogo en línea al momento de cargar la página. Confírmalos en la tienda antes de comprar.
                </p>
            </div>
        </section>
    );
};

export const Sizes = ({ section }) => (
    <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-24">
        <SectionTitle words={section.titleWords} text={section.text}>{section.title}</SectionTitle>
        <motion.div variants={fadeUp} {...inView} className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(RIDER_HEIGHTS).map(([size, [min, max]]) => (
                <div key={size} className="rounded-2xl border border-zinc-200 p-6 text-center hover:bg-black hover:text-white transition-colors">
                    <div className="text-4xl font-black italic">{size}</div>
                    <div className="mt-3 font-bold">{min}–{max} cm</div>
                    <div className="text-sm opacity-60">{cmToFeet(min)} – {cmToFeet(max)}</div>
                </div>
            ))}
        </motion.div>
        <p className="mt-6 text-sm text-gray-500">Fuente: {GEOMETRY_SOURCE}.</p>
    </section>
);

export const Steps = ({ section }) => (
    <section className="bg-zinc-50 py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <SectionTitle words={section.titleWords} text={section.text}>{section.title}</SectionTitle>
            <motion.ol {...inView} transition={{ staggerChildren: 0.15 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {section.items.map((item, index) => (
                    <motion.li key={item.title} variants={fadeUp} className="relative bg-white rounded-2xl p-8 border border-zinc-200">
                        <span className="block text-6xl font-black italic text-zinc-200 mb-4">{index + 1}</span>
                        <h3 className="text-lg font-black italic uppercase tracking-tight mb-3">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.text}</p>
                    </motion.li>
                ))}
            </motion.ol>
        </div>
    </section>
);

export const AccessoryCategories = ({ section, products }) => {
    const countFor = (match) => products.filter((p) => (p.category || '').trim().toLowerCase().includes(match.toLowerCase().slice(0, 9))).length;
    return (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24">
            <SectionTitle words={section.titleWords} text={section.text}>{section.title}</SectionTitle>
            <motion.div {...inView} transition={{ staggerChildren: 0.05 }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {section.items.map((item) => {
                    const Icon = ICONS[item.icon] || ShoppingBag;
                    const count = countFor(item.match);
                    return (
                        <motion.div key={item.label} variants={fadeUp}>
                            <Link to="/accesorios" className="group flex items-center gap-4 rounded-2xl border border-zinc-200 p-5 hover:bg-black hover:text-white hover:border-black transition-all">
                                <Icon className="w-7 h-7 shrink-0" />
                                <div>
                                    <div className="font-black uppercase tracking-tight leading-tight">{item.label}</div>
                                    {count > 0 && <div className="text-xs opacity-60">{count} productos</div>}
                                </div>
                            </Link>
                        </motion.div>
                    );
                })}
            </motion.div>
        </section>
    );
};

export const Accessories = ({ section, products }) => {
    const items = products.filter((p) => p.image_url && p.name).slice(0, 12);
    return (
        <section className="bg-zinc-50 py-24 overflow-hidden">
            <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                <SectionTitle words={section.titleWords} text={section.text}>{section.title}</SectionTitle>
            </div>
            {items.length > 0 && (
                <Swiper modules={[Navigation, Autoplay]} navigation autoplay={{ delay: 3000, disableOnInteraction: true }} loop={items.length > 5} spaceBetween={20} slidesPerView={1.5} breakpoints={{ 640: { slidesPerView: 2.5 }, 1024: { slidesPerView: 4.2 }, 1440: { slidesPerView: 5.2 } }} className="landing-bikes !px-6">
                    {items.map((product) => (
                        <SwiperSlide key={product.id}>
                            <Link to={`/product/${slugify(product.name)}`} className="group block bg-white rounded-2xl p-5 border border-zinc-200 hover:border-black transition-colors">
                                <div className="aspect-square flex items-center justify-center mb-4">
                                    <img loading="lazy" decoding="async" src={product.image_url} alt={product.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />
                                </div>
                                <h3 className="text-sm font-bold leading-snug line-clamp-2">{product.name}</h3>
                                <p className="text-xs text-gray-500 mt-1">{(product.category || '').trim()}</p>
                            </Link>
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}
            {section.cta && <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-12"><CtaButton cta={section.cta} /></div>}
        </section>
    );
};

export const Visit = ({ section }) => (
    <section className="bg-black text-white">
        <div className="grid grid-cols-1 lg:grid-cols-2">
            <motion.div variants={fadeUp} {...inView} className="px-6 md:px-16 py-24 flex flex-col justify-center">
                <h2 className="text-4xl md:text-6xl font-black uppercase italic tracking-tight leading-none mb-6"><TitleText text={section.title} words={section.titleWords} /></h2>
                <p className="text-zinc-400 text-lg leading-relaxed mb-10 max-w-lg">{section.text}</p>
                <ul className="space-y-5 mb-10">
                    <li className="flex gap-4"><MapPin className="w-5 h-5 mt-1 shrink-0" /><span>{business.streetAddress}<br />{business.addressLocality}, República Dominicana</span></li>
                    <li className="flex gap-4"><Phone className="w-5 h-5 mt-1 shrink-0" /><a href={`tel:${business.telephone}`} className="hover:underline">{business.telephoneDisplay}</a></li>
                    <li className="flex gap-4"><Clock className="w-5 h-5 mt-1 shrink-0" /><span>{business.hours.map((h) => <span key={h.label} className="block">{h.label}: {h.display}</span>)}</span></li>
                </ul>
                <div className="flex flex-col sm:flex-row gap-4">
                    <CtaButton cta={{ label: 'Cómo llegar', href: 'maps' }} light />
                    <CtaButton cta={{ label: 'WhatsApp', href: 'whatsapp', style: 'outline' }} light />
                </div>
            </motion.div>
            <div className="min-h-[420px] grayscale hover:grayscale-0 transition-all duration-700">
                <iframe
                    title={`Mapa de ${business.name}`}
                    src={`https://www.google.com/maps?q=${business.latitude},${business.longitude}&z=16&output=embed`}
                    className="w-full h-full min-h-[420px] border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                />
            </div>
        </div>
    </section>
);

export const Faq = ({ section }) => {
    const [open, setOpen] = useState(0);
    return (
        <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-24">
            <SectionTitle words={section.titleWords}>{section.title}</SectionTitle>
            <div>
                {section.items.map((item, index) => (
                    <div key={item.q} className="border-b border-zinc-200">
                        <button type="button" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} className="w-full flex items-center justify-between gap-6 py-6 text-left group">
                            <h3 className="text-lg md:text-xl font-bold group-hover:text-zinc-600 transition-colors">{item.q}</h3>
                            <Plus className={`w-6 h-6 shrink-0 transition-transform duration-300 ${open === index ? 'rotate-45' : ''}`} />
                        </button>
                        <AnimatePresence initial={false}>
                            {open === index && (
                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                                    <p className="pb-6 text-gray-600 leading-relaxed max-w-3xl">{item.a}</p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </div>
        </section>
    );
};

export const Related = ({ section }) => (
    <section className="bg-zinc-50 py-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <h2 className="text-2xl md:text-3xl font-black uppercase italic tracking-tight mb-10">{section.title}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {section.items.map((item) => (
                    <Link key={item.to} to={item.to} className="group flex items-center justify-between gap-4 bg-white rounded-2xl border border-zinc-200 p-6 hover:border-black transition-colors">
                        <div>
                            <div className="font-black uppercase tracking-tight">{item.label}</div>
                            <div className="text-sm text-gray-500">{item.text}</div>
                        </div>
                        <ArrowRight className="w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform" />
                    </Link>
                ))}
            </div>
        </div>
    </section>
);

export const Workshop = () => <ServiceMarketing />;
